export type Suspect = {
  id: string;
  resourceType: "Suspect";
  condition: string;
  code: string;
  documented: string;
};

export const exampleSuspects: Suspect[] = [
  {
    id: "suspect-1",
    resourceType: "Suspect",
    condition: "Diabetes Mellitus",
    code: "ICD-10: Z71.1",
    documented: "2023-10-15",
  },
  {
    id: "suspect-2",
    resourceType: "Suspect",
    condition: "Cardiovascular Condition",
    code: "ICD-10: Z03.89",
    documented: "2023-09-22",
  },
  {
    id: "suspect-3",
    resourceType: "Suspect",
    condition: "Endocrine Disorder",
    code: "ICD-10: Z03.9",
    documented: "2023-11-03",
  },
];

export const suspectMapping: Record<string, { displayName: string; logicTooltip: string }> = {
  // ──────────────────────────────
  // OLD MAPPINGS
  // ──────────────────────────────

  // ──────────────────────────────
  // Prediabetes
  // ──────────────────────────────
  prediabetes_hba1c_5_7_6_4: {
    displayName: "Prediabetes (HbA1c 5.7–6.4%)",
    logicTooltip:
      "HbA1c LOINC 4548-4 in LAB_RESULT with SOURCE_UNITS='%' and value 5.7–6.4; excludes patients already coded with R73.03.",
  },

  // ──────────────────────────────
  // Diabetes
  // ──────────────────────────────
  diabetes_hba1c_6_5_plus: {
    displayName: "Diabetes (HbA1c ≥ 6.5%)",
    logicTooltip:
      "HbA1c LOINC 4548-4 in LAB_RESULT with SOURCE_UNITS='%' and value ≥ 6.5; excludes patients already coded with diabetes (ICD-10 E08–E13).",
  },

  // ──────────────────────────────
  // COPD (airflow obstruction screen)
  // ──────────────────────────────
  copd_obstruction_postbd: {
    displayName: "COPD Obstruction (Post-BD FEV1/FVC < 0.70)",
    logicTooltip:
      "FEV1/FVC ratio LOINC 19926-5 in LAB_RESULT normalized to fraction; < 0.70 with post-bronchodilator indicated by code/text; excludes J44.*.",
  },
  copd_obstruction_unknown: {
    displayName: "COPD Obstruction (FEV1/FVC < 0.70, BD Unknown)",
    logicTooltip:
      "FEV1/FVC ratio LOINC 19926-5 normalized to fraction; < 0.70 when bronchodilator status is not explicit; excludes J44.*.",
  },

  // ──────────────────────────────
  // NEW MAPPINGS
  // ──────────────────────────────

  // ──────────────────────────────
  // Age-Related Macular Degeneration (AMD)
  // ──────────────────────────────
  amd: {
    displayName: "AMD Procedure with Reasoning",
    logicTooltip:
      "Flags when a comprehensive eye exam or retinal imaging (e.g., OCT, fundus photos, fluorescein angiography; CPT 92004, 92014, 92134, 92250, 92227–92229, 92235) is present and the documentation explicitly mentions “age-related.” Excludes patients already diagnosed with AMD (ICD-10 H35.3x).",
  },

  // ──────────────────────────────
  // Alcohol Use Disorder (BAL-based)
  // ──────────────────────────────
  alcohol_very_high_300plus: {
    displayName: "Alcohol Use — Very High BAL (≥300 mg/dL)",
    logicTooltip:
      "Ethanol LOINC 5643-2 normalized to mg/dL (mg/dL as-is; g/dL×1000; %×1000). Screen-positive if ≥300 mg/dL. Excludes patients with existing alcohol-related diagnoses (F10.*).",
  },
  alcohol_high_200plus: {
    displayName: "Alcohol Use — High BAL (200–299 mg/dL)",
    logicTooltip:
      "Ethanol LOINC 5643-2 normalized to mg/dL (mg/dL as-is; g/dL×1000; %×1000). Screen-positive if 200–299 mg/dL. Excludes patients with existing alcohol-related diagnoses (F10.*).",
  },
  alcohol_positive_80plus: {
    displayName: "Alcohol Use — Positive BAL (80–199 mg/dL)",
    logicTooltip:
      "Ethanol LOINC 5643-2 normalized to mg/dL (mg/dL as-is; g/dL×1000; %×1000). Screen-positive if 80–199 mg/dL. Excludes patients with existing alcohol-related diagnoses (F10.*).",
  },
  alcohol_sbirt_intervention: {
    displayName: "Alcohol Use — SBIRT/Brief Intervention (procedure-based)",
    logicTooltip:
      "Flags when a SBIRT/brief intervention procedure is billed (CPT/HCPCS 99408, 99409, G0396, G0397, G0443). Excludes patients already coded F10.*. Intended as strong evidence when BAL labs may be absent.",
  },

  // ──────────────────────────────
  // Amputation
  // ──────────────────────────────
  amputation_history: {
    displayName: "Amputation History (Procedure-based)",
    logicTooltip:
      "Presence of definitive limb amputation procedure in PROCEDURE (CPT 27590, 27880, 27886, 28800, 28805, 28810, 28820, 28825). Excludes patients already documented with amputation status (ICD-10 Z89.*).",
  },

  // ──────────────────────────────
  // Angina
  // ──────────────────────────────
  angina_troponin_elevated: {
    displayName: "Angina (Troponin Elevated Above URL)",
    logicTooltip:
      "Any troponin result in LAB_RESULT above the sex-specific 99th percentile upper reference limit (URL). Cutoffs: Troponin I (10839-9: M 26 ng/L, F 16 ng/L), High-sensitivity Troponin I (89579-7: M 34 ng/L, F 16 ng/L), Troponin T (6598-7: M 15.5 ng/L, F 9 ng/L), High-sensitivity Troponin T (67151-1: M 22 ng/L, F 14 ng/L). Excludes patients already diagnosed with angina (ICD-10 I20*).",
  },

  // ──────────────────────────────
  // Anorexia / Bulimia
  // ──────────────────────────────
  bmi_lte_17: {
    displayName: "Anorexia/Bulimia (BMI ≤ 17)",
    logicTooltip:
      "Flags when BMI (LOINC 39156-5) is ≤ 17 kg/m², using either a direct BMI result or BMI derived from same-day weight (29463-7) and height (8302-2). Excludes patients already diagnosed with eating disorders (ICD-10 F50.xx).",
  },

  // ──────────────────────────────
  // Aortic Aneurysm
  // ──────────────────────────────
  aortic_aneurysm: {
    displayName: "Aortic Aneurysm Procedure with Reasoning",
    logicTooltip:
      "Flags when any of the following are present: (1) Aorta-focused imaging (ultrasound/CTA/MRA) with aneurysm terms in the reason, procedure text, or note; (2) Prior aortic aneurysm repair (EVAR/TEVAR) codes; or (3) Aortic diameter ≥ 3.0 cm on an observation (units normalized to cm). Excludes patients already diagnosed with I71*.",
  },

  // ──────────────────────────────
  // Arrhythmia
  // ──────────────────────────────
  arrhythmia_procedure_evidence: {
    displayName: "Arrhythmia (Definitive Procedure Evidence)",
    logicTooltip:
      "Flags when PROCEDURE contains definitive arrhythmia interventions: electrical cardioversion (CPT 92960 external, 92961 internal) or insertable loop recorder events (CPT 33285 insertion, 33286 removal). Excludes patients already coded with arrhythmia (ICD-10-CM I47.*, I48.*, I49.*). General ECG/screening/monitoring codes are intentionally not used.",
  },

  // ──────────────────────────────
  // Arrhythmia (expanded evidence)
  // ──────────────────────────────
  arrhythmia_ekg_evidence: {
    displayName: "Arrhythmia (EKG Finding Evidence)",
    logicTooltip:
      "ECG/EKG evidence from OBSERVATION (LOINC display/category mentions ECG/EKG) where RESULT/NOTE_TEXT contains arrhythmia terms (e.g., atrial fibrillation/flutter, SVT, VT/VF, junctional rhythm, AV block/Mobitz/CHB, sick sinus, sinus pause, PVC/PAC) with simple negation guards (no 'no evidence of', 'without', 'normal sinus/NSR', 'no arrhythmia'). Also accepts ECG procedures (CPT 93000/93005/93010) when the reason SNOMED indicates arrhythmia. Excludes patients already coded with I47.*, I48.*, or I49.*.",
  },

  arrhythmia_med_antiarrhythmic: {
    displayName: "Arrhythmia (Anti-arrhythmic Treatment)",
    logicTooltip:
      "MedicationRequest linked to Medication for Class I/III anti-arrhythmics: quinidine, procainamide, flecainide, propafenone, amiodarone, sotalol, dofetilide (brands incl. Tikosyn, Rythmol, Tambocor, Pacerone/Cordarone, Betapace). AuthoredOn is used as event date. Excludes patients already coded with I47.*, I48.*, or I49.*.",
  },

  arrhythmia_med_beta_ccb_supportive: {
    displayName: "Arrhythmia (β-blocker/CCB + Objective Evidence)",
    logicTooltip:
      "MedicationRequest for propranolol, metoprolol, atenolol, verapamil, or diltiazem PLUS objective arrhythmia evidence within ±90 days (EKG finding as above or ECG procedure with arrhythmia reason; or definitive cardioversion/ILR CPT 92960/92961/33285/33286). Excludes patients already coded with I47.*, I48.*, or I49.*.",
  },

  // ──────────────────────────────
  // Asthma (bronchodilator reversibility)
  // ──────────────────────────────
  asthma_bdr_postbd: {
    displayName: "Asthma BDR (Post-BD same-day improvement)",
    logicTooltip:
      "Flags when same-day spirometry shows bronchodilator reversibility with an explicit post-bronchodilator result: FEV1 Δ ≥12% OR ≥200 mL, or PEF Δ ≥20%. Pairs values within the same patient+encounter+calendar date using LOINCs FEV1 pre/unspecified 20150-9, FEV1 post-BD 20155-8, and PEF 33452-4. Values are normalized (FEV1→L, PEF→L/min) and plausibility-checked. Excludes patients with existing asthma diagnoses (ICD-10 J45*).",
  },

  asthma_bdr_unknown: {
    displayName: "Asthma BDR (Same-day improvement, BD status unknown)",
    logicTooltip:
      "Flags when same-day spirometry shows reversibility without an explicit post-BD tag: uses min vs max on the same date for the metric. Thresholds: FEV1 Δ ≥12% OR ≥200 mL, or PEF Δ ≥20%. Uses LOINCs 20150-9 (FEV1) and 33452-4 (PEF); normalizes units (FEV1→L, PEF→L/min) and applies plausibility guards. Excludes patients with existing asthma diagnoses (ICD-10 J45*).",
  },

  // ──────────────────────────────
  // Barrett’s Esophagus
  // ──────────────────────────────
  barretts_esophagus: {
    displayName: "Barrett’s Esophagus (EGD or Pathology Signal)",
    logicTooltip:
      "Flags when EITHER: (1) EGD notes are consistent with Barrett’s (e.g., “Barrett’s,” or salmon-colored mucosa at the esophagus/GE junction), OR (2) pathology mentions intestinal metaplasia (e.g., goblet cells). Excludes patients already diagnosed with Barrett’s esophagus (ICD-10 K22.7*).",
  },

  // ──────────────────────────────
  // Benign Carcinoid (5-HIAA)
  // ──────────────────────────────
  benign_carcinoid_5hiaa_gt8: {
    displayName: "Benign Carcinoid (5-HIAA > 8 mg/24 h)",
    logicTooltip:
      "24-hour urinary 5-HIAA (LOINC 1695-6) normalized to mg/24 h. Screen-positive when value > 8 mg/24 h. Excludes patients already diagnosed with benign carcinoid (ICD-10-CM D3A.*).",
  },
  carcinoid_procedure_reason_support: {
    displayName: "NET/Carcinoid Procedure (Reason-coded)",
    logicTooltip:
      "Supportive evidence when a procedure (e.g., SSTR PET/CT 78812–78816, SPECT 78802–78803, or related tumor procedures) includes a SNOMED reason explicitly mentioning 'neuroendocrine' or 'carcinoid'. Excludes patients already coded with D3A.*.",
  },

  // ──────────────────────────────
  // Bipolar Disorder (medication signal)
  // ──────────────────────────────
  bipolar_lithium_signal: {
    displayName: "Bipolar (Lithium lab present)",
    logicTooltip:
      "Flags a screening signal when a Lithium level is present in LAB_RESULT (LOINC 14334-7). Excludes patients already diagnosed with bipolar disorder (ICD-10 F31*) and those with substance/medication-induced mood disorder (F10.14, F15.24, F19.14). Emits a minimal Observation FHIR for the supporting lab. Not diagnostic on its own.",
  },

  // ──────────────────────────────
  // (BP) Hypertension
  // ──────────────────────────────
  stage2_systolic: {
    displayName: "Hypertension Stage 2 (Systolic)",
    logicTooltip:
      "Single BP observation (OBSERVATION, LOINC 8480-6) with numeric value ≥ 140 mmHg. Excludes patients with existing hypertension ICD-10 codes (I10–I15). Screening signal only; not a diagnosis.",
  },
  stage2_diastolic: {
    displayName: "Hypertension Stage 2 (Diastolic)",
    logicTooltip:
      "Single BP observation (OBSERVATION, LOINC 8462-4) with numeric value ≥ 90 mmHg. Excludes patients with existing hypertension ICD-10 codes (I10–I15). Screening signal only; not a diagnosis.",
  },
  stage1_systolic: {
    displayName: "Hypertension Stage 1 (Systolic)",
    logicTooltip:
      "Single BP observation (OBSERVATION, LOINC 8480-6) with numeric value 130–139 mmHg. Excludes patients with existing hypertension ICD-10 codes (I10–I15). Screening signal only; not a diagnosis.",
  },
  stage1_diastolic: {
    displayName: "Hypertension Stage 1 (Diastolic)",
    logicTooltip:
      "Single BP observation (OBSERVATION, LOINC 8462-4) with numeric value 80–89 mmHg. Excludes patients with existing hypertension ICD-10 codes (I10–I15). Screening signal only; not a diagnosis.",
  },
  hypertension_antihypertensive_rx: {
    displayName: "Hypertension (Antihypertensive Combination Rx)",
    logicTooltip:
      "MedicationRequest indicates a specific antihypertensive combination (ACEI + thiazide, ARB + thiazide, ARB + DHP-CCB, ACEI + DHP-CCB, or triple ARB + DHP-CCB + thiazide) matched via RxNorm code list; uses AUTHORED_ON as event date. Excludes patients already diagnosed with hypertension (ICD-10 I10–I15).",
  },

  // ──────────────────────────────
  // Carotid Artery Stenosis
  // ──────────────────────────────
  cas_strong_evidence: {
    displayName: "Carotid Artery Stenosis (Strong Evidence)",
    logicTooltip:
      "Patient had either catheter cerebral angiography (CPT 36215) or carotid endarterectomy (CPT 35301), excluding anyone already diagnosed with carotid stenosis (ICD-10 I65.*).",
  },

  // ──────────────────────────────
  // Chronic Hepatitis C
  // ──────────────────────────────
  chronic_hepc_lab_positive: {
    displayName: "Chronic Hep C (Positive HCV Lab)",
    logicTooltip:
      "Any HCV lab in LAB_RESULT with LOINC 13955-0, 16128-1, 11011-4, 38180-6, 32286-7, or 48159-8 where a numeric value parses > 0 (no text parsing). Excludes patients already diagnosed with chronic hepatitis C (ICD-10-CM B18.2*).",
  },
  chronic_hepc_daa_treatment: {
    displayName: "Chronic Hep C (DAA Treatment Signal)",
    logicTooltip:
      "MEDICATION description (RxNorm or source) contains a direct-acting antiviral: sofosbuvir, ledipasvir, velpatasvir, voxilaprevir, glecaprevir, pibrentasvir, elbasvir, grazoprevir, daclatasvir, or simeprevir; brand support: Harvoni, Epclusa, Vosevi, Mavyret, Zepatier. Explicitly excludes ribavirin. Uses dispensing date, else prescribing date. Excludes patients already diagnosed with chronic hepatitis C (ICD-10-CM B18.2*).",
  },

  // ──────────────────────────────
  // Cirrhosis
  // ──────────────────────────────
  cirrhosis_liver_biopsy: {
    displayName: "Cirrhosis (Liver Biopsy Evidence)",
    logicTooltip:
      "Any liver biopsy CPT (47000, 47001, 47002, 47379, 47003). Excludes ICD-10-CM K74.*.",
  },
  cirrhosis_fib4_high: {
    displayName: "Cirrhosis (FIB-4 ≥ 3.25)",
    logicTooltip:
      "Same-day AST (1920-8), ALT (1742-6), and Platelets (777-3 or 26515-7). FIB-4 = (Age × AST) / (Platelets × √ALT); flag when ≥ 3.25. Excludes ICD-10-CM K74.*.",
  },

  // ──────────────────────────────
  // Chronic Kidney Disease (CKD) — highest stage only
  // ──────────────────────────────
  ckd_stage5: {
    displayName: "CKD Stage 5 (eGFR < 15 or Dialysis)",
    logicTooltip:
      "Emits only the highest CKD stage per patient. Stage 5 when either: (a) eGFR < 15 mL/min/1.73 m² on ≥2 distinct dates using LOINCs 33914-3/62238-1/69405-9/98979-8, or (b) any dialysis procedure code present. Excludes patients with existing CKD diagnoses (N18.*).",
  },
  ckd_stage4: {
    displayName: "CKD Stage 4 (eGFR 15–29)",
    logicTooltip:
      "Emits only the highest CKD stage per patient. Stage 4 when eGFR is 15–29 mL/min/1.73 m² on ≥2 distinct dates using LOINCs 33914-3/62238-1/69405-9/98979-8. Excludes patients with existing CKD diagnoses (N18.*).",
  },
  ckd_stage3b: {
    displayName: "CKD Stage 3b (eGFR 30–44)",
    logicTooltip:
      "Emits only the highest CKD stage per patient. Stage 3b when eGFR is 30–44 mL/min/1.73 m² on ≥2 distinct dates using LOINCs 33914-3/62238-1/69405-9/98979-8. Excludes patients with existing CKD diagnoses (N18.*).",
  },
  ckd_stage3a: {
    displayName: "CKD Stage 3a (eGFR 45–59)",
    logicTooltip:
      "Emits only the highest CKD stage per patient. Stage 3a when eGFR is 45–59 mL/min/1.73 m² on ≥2 distinct dates using LOINCs 33914-3/62238-1/69405-9/98979-8. Excludes patients with existing CKD diagnoses (N18.*).",
  },
  ckd_albuminuria: {
    displayName: "CKD Albuminuria Signal (ACR > 30 mg/g)",
    logicTooltip:
      "Emits only the highest CKD stage per patient (albuminuria is treated as a Stage 2 signal). Triggered when albumin/creatinine ratio (LOINC 9318-7) > 30 mg/g with units restricted to mg/g variants (e.g., 'mg/g', 'mg/g creat', 'mg/g{creat}', 'mg/g_creat'). Excludes patients with existing CKD diagnoses (N18.*).",
  },

  // ──────────────────────────────
  // COPD
  // ──────────────────────────────
  copd_postbd_ratio_under_0_70: {
    displayName: "COPD Screen (FEV1/FVC <= 0.70)",
    logicTooltip:
      "FEV1/FVC ratio using LOINC 19926-5 from LAB_RESULT. Flag when the normalized ratio <= 0.70. Excludes patients with existing COPD (ICD-10 J44.*).",
  },

  // ──────────────────────────────
  // Coronary Artery Disease (CAD)
  // ──────────────────────────────
  cad_troponin_i_high: {
    displayName: "CAD (Troponin I ≥ 40 ng/L)",
    logicTooltip:
      "Troponin I LOINC 10839-9 from LAB_RESULT normalized to ng/L (ng/mL×1000, µg/L×1000, pg/mL=ng/L, ng/L as-is) with non-empty units; flag when value ≥ 40 ng/L. Excludes patients already coded with CAD (ICD-10 I25.*).",
  },
  cad_troponin_t_high: {
    displayName: "CAD (Troponin T ≥ 14 ng/L)",
    logicTooltip:
      "Troponin T LOINC 6598-7 from LAB_RESULT normalized to ng/L (ng/mL×1000, µg/L×1000, pg/mL=ng/L, ng/L as-is) with non-empty units; flag when value ≥ 14 ng/L. Excludes patients already coded with CAD (ICD-10 I25.*).",
  },
  cad_prior_pci: {
    displayName: "CAD (History of PCI Stent)",
    logicTooltip:
      "Presence of percutaneous coronary intervention procedure codes CPT 92928 or 92929 in PROCEDURE. Excludes patients already coded with CAD (ICD-10 I25.*).",
  },
  cad_prior_cabg: {
    displayName: "CAD (History of CABG)",
    logicTooltip:
      "Presence of coronary artery bypass grafting procedure codes CPT 33511 or 33512 in PROCEDURE. Excludes patients already coded with CAD (ICD-10 I25.*).",
  },

  // ──────────────────────────────
  // CVA / Stroke History
  // ──────────────────────────────
  cva_history: {
    displayName: "History of Stroke (Imaging/Treatment Context)",
    logicTooltip:
      "Flags when neuroimaging (CT/MR/MRA/CTA) or reperfusion therapy (thrombectomy or thrombolytics) is present and documentation indicates stroke (e.g., “stroke”, “CVA”, ischemic or hemorrhagic stroke) in the reason, code text, or note. Excludes patients already documented with history of stroke (Z86.73) or sequelae of cerebrovascular disease (I69.x).",
  },

  // ──────────────────────────────
  // CVA / Stroke History — NIHSS Evidence
  // ──────────────────────────────
  cva_history_nihss: {
    displayName: "History of Stroke (NIHSS > 0)",
    logicTooltip:
      "NIH Stroke Scale total score > 0 from an NIHSS panel or total (LOINC 70182-1 or 72089-6). Used as supportive stroke evidence. Excludes patients already documented with history of stroke (Z86.73) or sequelae of cerebrovascular disease (I69.x).",
  },

  // ──────────────────────────────
  // Dementia (MoCA)
  // ──────────────────────────────
  dementia_moca_lt18: {
    displayName: "Dementia (MoCA < 18)",
    logicTooltip:
      "Uses MoCA results (LOINC 72172-0 or 72133-2). If multiple scores occur on the same day, the lowest score is used. Flags when the score is under 18. Excludes patients already diagnosed with dementia (F01–F03) or Alzheimer disease (G30).",
  },
  dementia_moca_18_21: {
    displayName: "Dementia (MoCA 18–21)",
    logicTooltip:
      "Uses MoCA results (LOINC 72172-0 or 72133-2). If multiple scores occur on the same day, the lowest score is used. Flags when the score is 18–21 (high-specificity range). Excludes patients already diagnosed with dementia (F01–F03) or Alzheimer disease (G30).",
  },
  dementia_nmda_memantine_rx: {
    displayName: "Dementia (NMDA antagonist treatment)",
    logicTooltip:
      "Flags a treatment signal when there is a MedicationRequest for memantine—generic or brand (Namenda, Namzaric). Matching is via RxNorm/NDC display text. Excludes other NMDA antagonists (e.g., ketamine, amantadine). Excludes patients already diagnosed with dementia (F01–F03) or Alzheimer disease (G30). Not a diagnosis—treatment, signal only.",
  },

  // ──────────────────────────────
  // Diabetes
  // ──────────────────────────────
  diabetes_glucose_200plus: {
    displayName: "Diabetes (Glucose ≥ 200 mg/dL)",
    logicTooltip:
      "Glucose LOINC 2345-7 from OBSERVATION normalized to mg/dL (mmol/L×18.0182; mg/dL as-is); flag when value ≥ 200 mg/dL. Excludes patients already coded with diabetes (ICD-10 E08–E13).",
  },
  diabetes_fpg_126_199: {
    displayName: "Diabetes (FPG 126–199 mg/dL)",
    logicTooltip:
      "Glucose LOINC 2345-7 from OBSERVATION normalized to mg/dL (mmol/L×18.0182; mg/dL as-is); flag when 126–199 mg/dL (assumes fasting if applicable). Excludes patients already coded with diabetes (ICD-10 E08–E13).",
  },
  diabetes_hba1c_6p5plus: {
    displayName: "Diabetes (HbA1c ≥ 6.5%)",
    logicTooltip:
      "HbA1c LOINC 4548-4 from LAB_RESULT using only rows with SOURCE_UNITS='%'; flag when value ≥ 6.5%. Excludes patients already coded with diabetes (ICD-10 E08–E13).",
  },
  diabetes_glucose_lowering_rx: {
    displayName: "Diabetes (Glucose-Lowering Medication)",
    logicTooltip:
      "MedicationRequest indicates a glucose-lowering agent: insulin (all forms), GLP-1 RAs for diabetes (excludes weight-loss-only brands Wegovy/Saxenda/Zepbound), SGLT2 inhibitors ± combos, DPP-4 inhibitors ± combos, sulfonylureas ± combos, TZDs ± combos, meglitinides ± combos, and alpha-glucosidase inhibitors. Metformin-only monotherapy is excluded; combos with metformin are included. Uses AUTHORED_ON as event date. Excludes patients already diagnosed with diabetes (ICD-10 E08–E13).",
  },

  // ──────────────────────────────
  // Diabetic Retinopathy
  // ──────────────────────────────
  diabetic_retinopathy: {
    displayName: "Diabetic Retinopathy (Eye Exam/Imaging Signal)",
    logicTooltip:
      "Flags during comprehensive eye exam or retinal imaging (e.g., CPT 92004, 92014, 92134, 92250, 92227, 92228, 92229, 92235) when documentation supports diabetic retinopathy. Accepts either: (1) a diabetes-linked retinopathy code (NPDR/PDR/DME; common SNOMED reasons allowed), or (2) free text that mentions both diabetes and retinopathy while not stating 'without/no/absent retinopathy'. Excludes patients already coded with diabetic retinopathy or diabetes with ophthalmic complications (H36.0*, E08.3*, E09.3*, E10.3*, E11.3*, E13.3*).",
  },

  // ──────────────────────────────
  // Heart Failure (multisignal)
  // ──────────────────────────────
  hf_combined_suspect: {
    displayName: "Heart Failure (BNP/NT-proBNP + Echo + Symptom)",
    logicTooltip:
      "Suspect raised only when ALL are present: (A) BNP > 100 pg/mL OR NT-proBNP > 300 pg/mL (LOINCs 30934-4/42637-9 for BNP; 33762-6/83107-3 for NT-proBNP), (B) an echocardiography procedure on record (e.g., 93306/93307/C8929 or description contains “echocardiography”), and (C) ≥1 symptom/diagnosis code: dyspnea (R06.0*), fatigue (R53.83), chest pain (R07.9*), edema (R60.*), or JVD (R09.89). Excludes patients already coded with I50.*.",
  },

  // ──────────────────────────────
  // Cancer (History / Active)
  // ──────────────────────────────
  cancer_treatment_history_active: {
    displayName: "Cancer Tx Evidence (Chemo / Radiation Procedures)",
    logicTooltip:
      "Flags when PROCEDURE indicates cancer treatment: chemotherapy administration CPT (96401, 96402, 96409, 96411, 96413, 96415–96417, 96420, 96422–96425, 96440, 96446, 96450) or IV infusion adjuncts (96366–96368); radiation therapy families (7726–7729, 7740–7743, 7752) or common RT codes (77014, 77373, 77385, 77412, 77427); or description contains 'chemotherapy', 'antineoplastic', 'radiation', 'radiotherapy', 'oncology', 'cancer treatment'. Screens out 'screen'/'assay' text and requires non-empty description. Excludes patients already coded with malignancy or history (ICD-10-CM C*, Z85.*).",
  },

  // ──────────────────────────────
  // Hyperlipidemia (screening)
  // ──────────────────────────────
  hyperlipidemia_totalchol_200plus: {
    displayName: "Hyperlipidemia (Total Cholesterol ≥ 200)",
    logicTooltip:
      "Single LAB_RESULT with total cholesterol LOINC 2093-3 normalized to mg/dL and value ≥ 200; excludes patients with existing hyperlipidemia ICD-10 E78.*.",
  },
  hyperlipidemia_ldl_130plus: {
    displayName: "Hyperlipidemia (LDL-C ≥ 130)",
    logicTooltip:
      "Single LAB_RESULT with LDL-C LOINC 2089-1/13457-7/18262-6/39469-2 normalized to mg/dL and value ≥ 130; excludes patients with existing hyperlipidemia ICD-10 E78.*.",
  },
  hyperlipidemia_tg_200plus: {
    displayName: "Hyperlipidemia (Triglycerides ≥ 200)",
    logicTooltip:
      "Single LAB_RESULT with triglycerides LOINC 2571-8 normalized to mg/dL and value ≥ 200; excludes patients with existing hyperlipidemia ICD-10 E78.*.",
  },
  hyperlipidemia_lipidlowering_med: {
    displayName: "Hyperlipidemia (lipid-lowering medication)",
    logicTooltip:
      "Presence of a MedicationRequest for a lipid-lowering agent — statin, PCSK9 inhibitor, bile-acid sequestrant, or fibrate — matched on RxNorm/NDC display (e.g., atorvastatin/rosuvastatin/simvastatin; evolocumab/alirocumab/inclisiran; cholestyramine/colesevelam/colestipol; fenofibrate/fenofibric acid/gemfibrozil). Excludes patients with existing hyperlipidemia ICD-10 E78.*.",
  },

  // ──────────────────────────────
  // Hyperparathyroidism (PTH + Calcium)
  // ──────────────────────────────
  hyperpara_pth_plus_calcium: {
    displayName: "Hyperparathyroidism (PTH + Calcium Elevated)",
    logicTooltip:
      "Requires BOTH: PTH LOINC 2731-8 normalized to pg/mL > 65 AND elevated calcium — either total Ca LOINC 17861-6 > 10.5 mg/dL (mmol/L×4.0), ionized Ca LOINC 1995-0 > 1.32 mmol/L, or ionized Ca LOINC 12180-6 > 5.2 mg/dL; excludes patients with existing ICD-10 E21.*.",
  },

  // ──────────────────────────────
  // Hypothyroidism
  // ──────────────────────────────
  hypothyroid_tsh_high_t4_low: {
    displayName: "Hypothyroidism (TSH High + T4 Low)",
    logicTooltip:
      "Requires BOTH: TSH LOINC 11580-8/3016-3/3015-5 > 4.5 mIU/L after unit normalization (mIU/L or µIU/mL ≡ mIU/L; IU/L ×1000) AND low T4 — either Free T4 LOINC 3024-7/6892-4 < 0.8 ng/dL (pmol/L ÷ 12.87) OR Total T4 LOINC 3026-2 < 5.1 µg/dL (nmol/L ÷ 12.9); excludes patients with existing ICD-10 E03.*.",
  },

  hypothyroid_levothyroxine_treatment: {
    displayName: "Hypothyroidism (Levothyroxine Treatment)",
    logicTooltip:
      "Medication evidence path. Flags when either a MedicationRequest (AUTHORED_ON) or MedicationStatement (START/END) joins to CORE_V3.CORE__MEDICATION indicating levothyroxine via RxNorm/NDC display or code—generic “levothyrox-” or brands (Synthroid, Levoxyl, Tirosint, Unithroid). Prefer active status when available. Excludes patients already diagnosed with hypothyroidism (ICD-10 E03.*).",
  },

  // ──────────────────────────────
  // Major Depression (screen positives)
  // ──────────────────────────────
  depression_phq9_10plus: {
    displayName: "Depression Screen Positive (PHQ-9 ≥ 10)",
    logicTooltip:
      "PHQ-9 total score LOINC 44261-6 ≥ 10 (plausible range 0–27); excludes patients with existing depressive disorder codes (ICD-10 F32.*, F33.*, F34.1).",
  },
  depression_phq2_3plus: {
    displayName: "Depression Screen Positive (PHQ-2 ≥ 3)",
    logicTooltip:
      "PHQ-2 total score LOINC 55758-7 ≥ 3 (plausible range 0–6); excludes patients with existing depressive disorder codes (ICD-10 F32.*, F33.*, F34.1).",
  },
  depression_ssri_treatment: {
    displayName: "Depression (SSRI Treatment)",
    logicTooltip:
      "MedicationRequest indicates an SSRI via an explicit RxNorm whitelist: citalopram/Celexa, escitalopram/Lexapro, fluoxetine/Prozac, sertraline/Zoloft, paroxetine/Paxil/Pexeva (incl. CR/mesylate), fluvoxamine; also vilazodone/Viibryd and vortioxetine/Trintellix. Uses AUTHORED_ON as event date. Excludes patients already diagnosed with depressive disorders (ICD-10 F32*, F33*, F341) and those with bipolar or primary psychotic disorders (F30*, F31*, F20*, F22*, F23*, F25*, F28*, F29*). Emits a minimal MedicationRequest FHIR; not diagnostic on its own.",
  },

  // ──────────────────────────────
  // Morbid Obesity
  // ──────────────────────────────
  morbid_obesity: {
    displayName: "Morbid Obesity (Direct BMI)",
    logicTooltip:
      "BMI LOINC 39156-5 ≥ 35 kg/m²; excludes patients with existing obesity diagnoses (ICD-10 E66.01, E66.2, E66.813).",
  },
  morbid_obesity_derived: {
    displayName: "Morbid Obesity (Derived BMI)",
    logicTooltip:
      "Derived BMI ≥ 35 kg/m² using same-encounter weight (LOINC 29463-7) normalized to kg and height (LOINC 8302-2) normalized to meters; excludes patients with existing obesity diagnoses (ICD-10 E66.01, E66.2, E66.813).",
  },

  // ──────────────────────────────
  // Osteoporosis / Osteopenia
  // ──────────────────────────────
  osteoporosis: {
    displayName: "Osteoporosis (DXA T-score ≤ −2.5)",
    logicTooltip:
      "Uses DXA T-scores (LOINC 38267-1 lumbar spine, 80948-3 femur-left, 80946-7 hip-left). On each calendar day, takes the lowest T-score and flags if ≤ −2.5. Excludes patients already coded with osteoporosis or osteopenia (ICD-10 M80*, M81*, M85.8*).",
  },
  osteopenia: {
    displayName: "Osteopenia (DXA T-score −2.5 < T ≤ −1.0)",
    logicTooltip:
      "Uses DXA T-scores (LOINC 38267-1 lumbar spine, 80948-3 femur-left, 80946-7 hip-left). On each calendar day, takes the lowest T-score and flags when −2.5 < T-score ≤ −1.0. Excludes patients already coded with osteoporosis or osteopenia (ICD-10 M80*, M81*, M85.8*).",
  },

  // ──────────────────────────────
  // Obstructive Sleep Apnea
  // ──────────────────────────────
  osa_sleep_study: {
    displayName: "Obstructive Sleep Apnea (Sleep Study Evidence)",
    logicTooltip:
      "Flags when PROCEDURE shows a sleep study or home sleep test: CPT 95810, 95811, 95800, 95806, 95807 or HCPCS G0399, G0400. Excludes patients with confounding diagnoses: heart failure (ICD-10-CM I50.*) or ARDS (J80.*).",
  },

  osa_pap_treatment: {
    displayName: "Obstructive Sleep Apnea (PAP/CPAP Evidence)",
    logicTooltip:
      "Flags when PROCEDURE indicates PAP/CPAP device or management: HCPCS E0601 or CPT 94660. Excludes patients with confounding diagnoses: heart failure (ICD-10-CM I50.*) or ARDS (J80.*).",
  },

  // ──────────────────────────────
  // Ostomy
  // ──────────────────────────────
  ostomy_colostomy: {
    displayName: "Ostomy — Colostomy",
    logicTooltip:
      "Definitive procedure indicating colostomy creation (CPT 44320 or 44188); excludes patients already coded with Z93.*.",
  },
  ostomy_gastrostomy: {
    displayName: "Ostomy — Gastrostomy",
    logicTooltip:
      "Definitive procedure indicating gastrostomy tube insertion/replacement (CPT 43246, 43762, or 49440); excludes patients already coded with Z93.*.",
  },
  ostomy_tracheostomy: {
    displayName: "Ostomy — Tracheostomy",
    logicTooltip:
      "Definitive procedure indicating tracheostomy status/procedure (CPT 31615); excludes patients already coded with Z93.*.",
  },
  ostomy_nephrostomy: {
    displayName: "Ostomy — Nephrostomy",
    logicTooltip:
      "Definitive procedure indicating nephrostomy catheter placement (CPT 50432); excludes patients already coded with Z93.*.",
  },
  ostomy_cholecystostomy: {
    displayName: "Ostomy — Cholecystostomy",
    logicTooltip:
      "Definitive procedure indicating cholecystostomy placement (CPT 47490); excludes patients already coded with Z93.*.",
  },
  ostomy_cystostomy: {
    displayName: "Ostomy — Cystostomy",
    logicTooltip:
      "Definitive procedure indicating cystostomy tube change/maintenance (CPT 51705); excludes patients already coded with Z93.*.",
  },

  // ──────────────────────────────
  // Peripheral Artery Disease (PAD)
  // ──────────────────────────────
  pad_revasc_history: {
    displayName: "PAD — Prior Revascularization",
    logicTooltip:
      "History of extremity revascularization procedure (e.g., peripheral angioplasty CPT 37226 or atherectomy CPT 37221); excludes patients already coded with PAD (I70.2*).",
  },

  // ──────────────────────────────
  // Prediabetes
  // ──────────────────────────────
  prediabetes_hba1c: {
    displayName: "Prediabetes (HbA1c 5.7–6.4%)",
    logicTooltip:
      "HbA1c LOINC 4548-4 with SOURCE_UNITS='%' and value 5.7–6.4; excludes patients already coded with R73.03.",
  },
  prediabetes_fpg: {
    displayName: "Prediabetes (FPG 100–125 mg/dL)",
    logicTooltip:
      "Glucose LOINC 2345-7 normalized to mg/dL in the 100–125 range (mmol/L × 18.0182 if needed); excludes patients already coded with R73.03.",
  },
  prediabetes_ogtt: {
    displayName: "Prediabetes (OGTT 140–199 mg/dL)",
    logicTooltip:
      "Glucose LOINC 2345-7 normalized to mg/dL in the 140–199 range (screening proxy when timing not explicit); excludes patients already coded with R73.03.",
  },

  // ──────────────────────────────
  // Pulmonary Nodule
  // ──────────────────────────────
  lung_nodule: {
    displayName: "Pulmonary Nodule (CT or Biopsy Evidence)",
    logicTooltip:
      "Flags when a chest CT/low-dose CT or lung biopsy is present and documentation indicates a lung nodule. Excludes patients already coded with pulmonary nodule (R91.1) or other abnormal lung imaging (R91.8).",
  },

  // ──────────────────────────────
  // Retinal Vein Occlusion (RVO)
  // ──────────────────────────────
  rvo_imaging_plus_injection: {
    displayName: "Retinal Vein Occlusion (Imaging + Intravitreal Injection)",
    logicTooltip:
      "Requires FA (92235) or OCT/OCTA (92134, 92137) AND intravitreal injection (67028) in PROCEDURE. Limits to RVO-related reasons (SNOMED: 786050007, 786049007, 24596005, 232039004, 232048009). Fundus imaging (92250, 92227, 92228) may be included as supporting. Excludes patients already diagnosed with retinal vascular occlusion (ICD-10 H348*).",
  },
  rvo_rx: {
    displayName: "Retinal Vein Occlusion (Anti-VEGF Treatment)",
    logicTooltip:
      "MedicationRequest matches an intravitreal anti-VEGF agent specific to RVO via RxNorm whitelist: aflibercept 2 mg (1232159, 2693567, 1232154, 2693564) or ranibizumab 0.5 mg including ranibizumab-nuna (643193, 1864423, 2602353). Uses AUTHORED_ON as event date. Excludes patients already diagnosed with retinal vascular occlusion (ICD-10 H348*). Not a diagnosis—treatment signal only.",
  },

  // ──────────────────────────────
  // Seizure Disorder / Epilepsy
  // ──────────────────────────────
  seizure_disorder: {
    displayName: "Seizure Disorder / Epilepsy (EEG/AED/Report Evidence)",
    logicTooltip:
      "Flags when an EEG is performed, a parenteral anti-epileptic (e.g., IV levetiracetam) is given, or an EEG report is present (LOINC 11523-8 or 92050-4) and documentation mentions seizure/epilepsy. Assigns G41 when “status epilepticus” is indicated; otherwise G40. Excludes patients already coded with epilepsy/recurrent seizures (G40.*), status epilepticus (G41.*), or personal history Z86.69.",
  },

  // ──────────────────────────────
  // Thyroid Nodule
  // ──────────────────────────────
  thyroid_nodule: {
    displayName: "Thyroid Nodule (US/FNA Evidence)",
    logicTooltip:
      "Flags when a thyroid/neck ultrasound or thyroid FNA/biopsy is recorded and the documentation indicates a thyroid nodule (or the body site is the thyroid). Excludes patients already diagnosed with nontoxic single or multinodular goiter or toxic nodular disease (ICD-10 E04.1, E04.2, E05.1, E05.2).",
  },
};
